import React, { useState, useEffect } from 'react';
const MaintenancePage = ({ maintenanceConfig, onRetry, onTestUserLogin, API }) => {
  const {
    message = "We're currently performing scheduled maintenance",
    details = "We'll be back shortly. Thank you for your patience!",
    icon = "🔧",
    backgroundType = "gradient",
    backgroundValue = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    scheduledEnd = null,
    customCSS = "",
    showCountdown = true,
    enableRetry = true,
    staffAccessTitle = "Staff Access",
    staffAccessEnabled = true,
    staffCaptchaEnabled = true,
    staffCaptchaQuestions = [
      { question: "What color is the sky?", answer: "blue" },
      { question: "How many days in a week?", answer: "7" },
      { question: "2 + 2 = ?", answer: "4" }
    ]
  } = maintenanceConfig || {};

  const [timeLeft, setTimeLeft] = React.useState(null);
  const [testEmail, setTestEmail] = React.useState('');
  const [testPassword, setTestPassword] = React.useState('');
  const [loginError, setLoginError] = React.useState('');
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  
  // CAPTCHA state
  const [captchaQuestion, setCaptchaQuestion] = React.useState(null);
  const [captchaAnswer, setCaptchaAnswer] = React.useState('');
  const [captchaError, setCaptchaError] = React.useState('');
  const [captchaInitialized, setCaptchaInitialized] = React.useState(false);

  // Initialize CAPTCHA only once on mount
  React.useEffect(() => {
    if (!captchaInitialized && staffCaptchaEnabled && staffCaptchaQuestions && staffCaptchaQuestions.length > 0) {
      const randomIndex = Math.floor(Math.random() * staffCaptchaQuestions.length);
      setCaptchaQuestion(staffCaptchaQuestions[randomIndex]);
      setCaptchaInitialized(true);
    }
  }, [captchaInitialized, staffCaptchaEnabled]);

  const refreshCaptcha = () => {
    if (staffCaptchaQuestions && staffCaptchaQuestions.length > 0) {
      const randomIndex = Math.floor(Math.random() * staffCaptchaQuestions.length);
      setCaptchaQuestion(staffCaptchaQuestions[randomIndex]);
      setCaptchaAnswer('');
      setCaptchaError('');
    }
  };

  const verifyCaptcha = () => {
    if (!staffCaptchaEnabled || !captchaQuestion) return true;
    const userAnswer = captchaAnswer.trim().toLowerCase();
    const correctAnswer = captchaQuestion.answer.toLowerCase();
    return userAnswer === correctAnswer;
  };

  React.useEffect(() => {
    if (!showCountdown || !scheduledEnd) return;

    const calculateTimeLeft = () => {
      try {
        const endTime = new Date(scheduledEnd);
        const now = new Date();
        const difference = endTime - now;

        if (difference > 0) {
          const hours = Math.floor(difference / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);
          return { hours, minutes, seconds, total: difference };
        }
      } catch (e) {
        console.error('Error calculating time:', e);
      }
      return null;
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      // Auto-retry when maintenance ends
      if (!newTimeLeft && enableRetry) {
        setTimeout(() => onRetry && onRetry(), 2000);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [scheduledEnd, showCountdown, enableRetry, onRetry]);

  const getBackgroundStyle = () => {
    if (backgroundType === 'image') {
      return {
        backgroundImage: `url(${backgroundValue})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    } else if (backgroundType === 'color') {
      return { backgroundColor: backgroundValue };
    } else {
      return { background: backgroundValue };
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={getBackgroundStyle()}
    >
      {customCSS && <style>{customCSS}</style>}
      
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 text-center maintenance-card">
        {/* Icon */}
        <div className="text-7xl md:text-9xl mb-6 animate-bounce">
          {icon}
        </div>

        {/* Message */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
          {message}
        </h1>

        {/* Details */}
        <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8">
          {details}
        </p>

        {/* Countdown Timer */}
        {showCountdown && timeLeft && timeLeft.total > 0 && (
          <div className="mb-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Expected completion time:</p>
            <div className="flex justify-center gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-lg p-4 min-w-[80px]">
                <div className="text-3xl font-bold">{timeLeft.hours}</div>
                <div className="text-xs uppercase mt-1">Hours</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-lg p-4 min-w-[80px]">
                <div className="text-3xl font-bold">{timeLeft.minutes}</div>
                <div className="text-xs uppercase mt-1">Minutes</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-lg p-4 min-w-[80px]">
                <div className="text-3xl font-bold">{timeLeft.seconds}</div>
                <div className="text-xs uppercase mt-1">Seconds</div>
              </div>
            </div>
          </div>
        )}

        {/* Retry Button */}
        {enableRetry && (
          <button
            onClick={onRetry}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
          >
            🔄 Retry Connection
          </button>
        )}

        {/* Staff Access Login Section */}
        {onTestUserLogin && API && staffAccessEnabled && (
          <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg max-w-sm mx-auto">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">🔐 {staffAccessTitle}</h3>
            <input
              type="email"
              placeholder="Email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full px-3 py-2 mb-2 border rounded-lg text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
            <input
              type="password"
              placeholder="Password"
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              className="w-full px-3 py-2 mb-2 border rounded-lg text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
            
            {/* CAPTCHA Section */}
            {staffCaptchaEnabled && captchaQuestion && (
              <div className="mb-2 p-2 bg-gray-200 dark:bg-gray-600 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-300">Security Question:</span>
                  <button 
                    onClick={refreshCaptcha}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    🔄 New Question
                  </button>
                </div>
                <p className="text-sm font-medium text-gray-800 dark:text-white mb-1">{captchaQuestion.question}</p>
                <input
                  type="text"
                  placeholder="Your answer"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  className="w-full px-3 py-1.5 border rounded text-sm dark:bg-gray-500 dark:border-gray-400 dark:text-white"
                />
                {captchaError && (
                  <p className="text-red-500 text-xs mt-1">{captchaError}</p>
                )}
              </div>
            )}
            
            {loginError && (
              <p className="text-red-500 text-xs mb-2">{loginError}</p>
            )}
            <button
              onClick={async () => {
                // Verify CAPTCHA first
                if (staffCaptchaEnabled && captchaQuestion) {
                  if (!verifyCaptcha()) {
                    setCaptchaError('Incorrect answer. Please try again.');
                    refreshCaptcha();
                    return;
                  }
                }
                
                setIsLoggingIn(true);
                setLoginError('');
                setCaptchaError('');
                try {
                  const formData = new FormData();
                  formData.append('email', testEmail);
                  formData.append('password', testPassword);
                  
                  const response = await fetch(`${API}/auth/login`, {
                    method: 'POST',
                    body: formData
                  });
                  const data = await response.json();
                  if (response.ok && data.user) {
                    // Check if this is the test user
                    if (data.user.email === 'mydarija@yahoo.com') {
                      onTestUserLogin(data);
                    } else {
                      setLoginError('Only authorized staff can login during maintenance.');
                      refreshCaptcha();
                    }
                  } else {
                    setLoginError(data.detail || 'Login failed');
                    refreshCaptcha();
                  }
                } catch (err) {
                  setLoginError('Connection error. Please try again.');
                  refreshCaptcha();
                }
                setIsLoggingIn(false);
              }}
              disabled={isLoggingIn}
              className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </button>
          </div>
        )}

        {/* Loading Animation */}
        <div className="mt-8 flex justify-center gap-2">
          <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};


export default MaintenancePage;
